import { useQuery } from '@tanstack/react-query'
import { ResponsiveContainer, AreaChart, Area, Tooltip as RTooltip, XAxis, YAxis } from 'recharts'
import { ArrowDown, ArrowUp, ExternalLink } from 'lucide-react'
import { api } from '@/lib/api'
import { cn, formatCompact } from '@/lib/utils'

interface Series {
  date: string
  sessions: number
  users: number
  pageviews: number
  engagement: number
}

export function AnalyticsPage() {
  const { data: overview } = useQuery({ queryKey: ['analytics.overview'], queryFn: api.analyticsOverview })
  const { data: topPages = [] } = useQuery({ queryKey: ['analytics.topPages'], queryFn: api.topPages })
  const { data: gsc } = useQuery({ queryKey: ['analytics.gsc'], queryFn: api.gsc })
  const { data: seo } = useQuery({ queryKey: ['analytics.seoPulse'], queryFn: api.seoPulse })

  const series: Series[] =
    overview?.rows.map((r) => ({
      date: r.dimensionValues[0].value,
      sessions: Number(r.metricValues[0].value),
      users: Number(r.metricValues[1].value),
      pageviews: Number(r.metricValues[2].value),
      engagement: Number(r.metricValues[3].value),
    })) ?? []

  const tot = overview?.totals
  const avgEngagement = series.length
    ? series.reduce((s, r) => s + r.engagement, 0) / series.length
    : 0

  const half = Math.floor(series.length / 2)
  const sumMetric = (arr: Series[], k: keyof Series) =>
    arr.reduce((s, r) => s + Number(r[k]), 0)
  const recent = series.slice(half)
  const prior = series.slice(0, half)
  const delta = (k: keyof Series) => {
    const a = sumMetric(recent, k)
    const b = sumMetric(prior, k)
    if (!b) return 0
    return ((a - b) / b) * 100
  }

  return (
    <div className="p-3 md:p-5 max-w-[1400px] mx-auto flex flex-col gap-3.5">
      <header>
        <h1 className="text-xl font-medium">Analytics</h1>
        <p className="text-xs text-ink-secondary mt-0.5">
          Last 28 days · organic traffic from Google Analytics and search queries from Search Console.
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          label="Sessions"
          value={formatCompact(tot?.sessions ?? 0)}
          delta={delta('sessions')}
          series={series}
          dataKey="sessions"
          color="#534AB7"
        />
        <Tile
          label="Users"
          value={formatCompact(tot?.users ?? 0)}
          delta={delta('users')}
          series={series}
          dataKey="users"
          color="#1D9E75"
        />
        <Tile
          label="Pageviews"
          value={formatCompact(tot?.pageviews ?? 0)}
          delta={delta('pageviews')}
          series={series}
          dataKey="pageviews"
          color="#EF9F27"
        />
        <Tile
          label="Avg engagement"
          value={`${avgEngagement.toFixed(0)}s`}
          delta={delta('engagement')}
          series={series}
          dataKey="engagement"
          color="#378ADD"
        />
      </div>

      <section className="border border-border rounded-md bg-surface-card p-4">
        <div className="flex justify-between items-baseline mb-2">
          <h2 className="text-sm font-medium">Organic sessions · 28 days</h2>
          <span className="text-[11px] text-ink-tertiary">GA4 · source = organic</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="sessionsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#534AB7" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="#534AB7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#888780' }}
                tickFormatter={(d: string) => d.slice(5)}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#888780' }}
                tickFormatter={(v) => formatCompact(Number(v))}
                axisLine={false}
                tickLine={false}
              />
              <RTooltip
                contentStyle={{
                  fontSize: 12,
                  border: '1px solid var(--border-default)',
                  borderRadius: 8,
                  background: 'var(--surface-card)',
                  color: 'var(--ink-primary)',
                }}
                formatter={(v: number) => formatCompact(v)}
              />
              <Area
                type="monotone"
                dataKey="sessions"
                stroke="#534AB7"
                strokeWidth={1.5}
                fill="url(#sessionsFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 min-w-0">
        <section className="border border-border rounded-md bg-surface-card">
          <div className="px-4 py-3 border-b border-border flex justify-between items-baseline">
            <h2 className="text-sm font-medium">Top pages</h2>
            <span className="text-[11px] text-ink-tertiary">sessions · last 28d</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-ink-tertiary border-b border-border">
                <th className="text-left px-4 py-2 font-normal">Page</th>
                <th className="text-right px-2 py-2 font-normal">Sessions</th>
                <th className="text-right px-2 py-2 font-normal">Users</th>
                <th className="text-right px-4 py-2 font-normal">Engagement</th>
              </tr>
            </thead>
            <tbody>
              {topPages.map((p) => (
                <tr key={p.page} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 truncate max-w-[260px]">
                    <div className="flex items-center gap-1 text-xs text-ink-secondary truncate">
                      <span className="truncate">{p.page}</span>
                      <ExternalLink size={11} className="shrink-0 text-ink-tertiary" />
                    </div>
                  </td>
                  <td className="px-2 py-2.5 text-right text-xs tabular-nums font-medium">
                    {formatCompact(p.sessions)}
                  </td>
                  <td className="px-2 py-2.5 text-right text-xs tabular-nums">
                    {formatCompact(p.users)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs tabular-nums text-ink-secondary">
                    {p.avg_engagement_seconds.toFixed(0)}s
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="border border-border rounded-md bg-surface-card">
          <div className="px-4 py-3 border-b border-border flex justify-between items-baseline">
            <h2 className="text-sm font-medium">Top search queries</h2>
            <span className="text-[11px] text-ink-tertiary">GSC · clicks</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-ink-tertiary border-b border-border">
                <th className="text-left px-4 py-2 font-normal">Query</th>
                <th className="text-right px-2 py-2 font-normal">Clicks</th>
                <th className="text-right px-2 py-2 font-normal">Impr</th>
                <th className="text-right px-4 py-2 font-normal">Pos</th>
              </tr>
            </thead>
            <tbody>
              {(gsc?.rows ?? []).slice(0, 8).map((r, i) => (
                <tr key={`${r.keys[0]}-${i}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 text-sm truncate max-w-[220px]" title={r.keys[0]}>
                    {r.keys[0]}
                  </td>
                  <td className="px-2 py-2.5 text-right text-xs tabular-nums font-medium">
                    {r.clicks}
                  </td>
                  <td className="px-2 py-2.5 text-right text-xs tabular-nums text-ink-secondary">
                    {formatCompact(r.impressions)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs tabular-nums">
                    {r.position.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {seo && (
        <section className="border border-border rounded-md bg-surface-card p-4">
          <h2 className="text-sm font-medium mb-3">Top movers · sessions vs prior 14d</h2>
          <div className="flex flex-col gap-2">
            {seo.top_movers.map((m) => (
              <div key={m.page} className="flex items-center gap-2.5">
                <span className="text-xs truncate flex-1">{m.page}</span>
                <span
                  className={cn(
                    'text-xs tabular-nums flex items-center gap-0.5',
                    m.delta_pct >= 0 ? 'text-status-approved-fg' : 'text-status-danger-fg',
                  )}
                >
                  {m.delta_pct >= 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                  {Math.abs(m.delta_pct)}%
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function Tile({
  label,
  value,
  delta,
  series,
  dataKey,
  color,
}: {
  label: string
  value: string
  delta: number
  series: Series[]
  dataKey: keyof Series
  color: string
}) {
  const isUp = delta >= 0
  return (
    <div className="border border-border rounded-md bg-surface-card p-3">
      <div className="text-[11px] text-ink-tertiary uppercase tracking-wider">{label}</div>
      <div className="text-xl font-medium mt-1 tabular-nums">{value}</div>
      <div
        className={cn(
          'text-[11px] flex items-center gap-0.5 mt-0.5',
          isUp ? 'text-status-approved-fg' : 'text-status-danger-fg',
        )}
      >
        {isUp ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
        {Math.abs(delta).toFixed(1)}% vs prior 14d
      </div>
      <div className="h-10 mt-1.5 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={1.5}
              fill={color}
              fillOpacity={0.12}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
